import React, { useState, useEffect } from 'react';
import { listZones, createZone, updateZone, deleteZone } from '../../services/adminMarketplaceService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import statesData from '../../data/nigeriaStatesLgas.json';

const emptyForm = { state: '', lga: '' };

const ZoneManager = () => {
  const toast = useToast();
  const [zones, setZones] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const refresh = () => listZones().then(setZones);
  useEffect(() => { refresh(); }, []);

  const lgasForState = statesData.find(s => s.name === form.state)?.lgas || [];

  const startEdit = (zone) => {
    setEditingId(zone._id);
    setForm({ state: zone.state, lga: zone.lga });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await updateZone(editingId, form);
        toast.success('Zone updated');
      } else {
        await createZone(form);
        toast.success('Zone added');
      }
      cancelEdit();
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save zone');
    }
  };

  const togglePause = async (zone) => {
    await updateZone(zone._id, { status: zone.status === 'active' ? 'paused' : 'active' });
    toast.success(zone.status === 'active' ? 'Zone paused' : 'Zone reactivated');
    refresh();
  };

  const confirmDelete = async () => {
    try {
      await deleteZone(deleteTarget._id);
      toast.success('Zone deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete zone');
    } finally {
      setDeleteTarget(null);
    }
  };

  const visibleZones = stateFilter ? zones.filter(z => z.state === stateFilter) : zones;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Zones</h2>
      <p className="text-sm text-gray-500">
        All 36 states + FCT and their local governments are pre-loaded. Pause a zone to stop it appearing for new bookings without losing the record; delete is only allowed once no therapist serves it.
      </p>

      <form onSubmit={handleSubmit} className="bg-white p-4 rounded-lg shadow grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value, lga: '' })}
          className="p-2 border rounded-lg" required>
          <option value="">State</option>
          {statesData.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        <select value={form.lga} onChange={(e) => setForm({ ...form, lga: e.target.value })}
          disabled={!form.state} className="p-2 border rounded-lg disabled:bg-gray-100" required>
          <option value="">Local government</option>
          {lgasForState.map(lga => <option key={lga} value={lga}>{lga}</option>)}
        </select>
        <div className="flex gap-2">
          <button type="submit" className="bg-black text-white px-4 py-2 rounded-lg">
            {editingId ? 'Save changes' : 'Add zone'}
          </button>
          {editingId && (
            <button type="button" onClick={cancelEdit} className="px-4 py-2 rounded-lg border">Cancel</button>
          )}
        </div>
        {error && <p className="sm:col-span-3 text-red-500 text-sm">{error}</p>}
      </form>

      <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="p-2 border rounded-lg text-sm">
        <option value="">Filter by state (all)</option>
        {statesData.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
      </select>

      <div className="bg-white rounded-lg shadow divide-y max-h-[32rem] overflow-y-auto">
        {visibleZones.map(z => (
          <div key={z._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3">
            <div>
              <p className="font-medium">
                {z.lga}, {z.state}
                {z.status === 'paused' && <span className="text-xs text-amber-600 ml-2">(paused)</span>}
              </p>
              <p className="text-xs text-gray-500">
                {z.therapistCount} therapist{z.therapistCount === 1 ? '' : 's'} serving this zone
                {z.createdBy && ' · added by a therapist'}
              </p>
            </div>
            <div className="flex gap-3 text-sm">
              <button onClick={() => startEdit(z)} className="text-blue-600">Edit</button>
              <button onClick={() => togglePause(z)} className="text-gray-600">
                {z.status === 'active' ? 'Pause' : 'Reactivate'}
              </button>
              <button
                onClick={() => setDeleteTarget(z)}
                disabled={z.therapistCount > 0}
                className="text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                title={z.therapistCount > 0 ? 'In use by a therapist — pause it instead' : ''}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        {visibleZones.length === 0 && <p className="text-gray-500 p-3">No zones found.</p>}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete this zone?"
        message={`${deleteTarget?.lga}, ${deleteTarget?.state} will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default ZoneManager;
